import { PASSWORD_RESET_VALIDITY_HOURS } from '@daymade/common-lib/constants';
import clsx from 'clsx';
import Button from 'components/common/Button';
import ErrorMessage from 'components/enter2026late/ErrorMessage';
import SlidingModal, {
  Props as SlidingModalProps,
} from 'components/enter2026late/SlidingModal';
import { ReactNode, useEffect, useId, useState } from 'react';
import {
  passwordResetErrorMessageFallback,
  passwordResetFriendlyErrorMessages,
} from 'utils/api/authApi';
import { tryApi } from 'utils/api/common';
import { authApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import { validEmail } from 'utils/RegistrationValidator';

import styles from './ForgottenPasswordSlidingModal.module.css';

interface UseForgottenPasswordSlidingModalOptions {
  slideAmount?: SlidingModalProps['slideAmount'];
  onRegisterClick: () => void;
}

export const useForgottenPasswordSlidingModal = (
  options: UseForgottenPasswordSlidingModalOptions,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');

  return {
    render: () => (
      <ForgottenPasswordSlidingModal
        emailAddress={emailAddress}
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        slideAmount={options.slideAmount}
        onRegisterClick={options.onRegisterClick}
      />
    ),
    open: (emailAddress: string) => {
      setEmailAddress(emailAddress);
      setIsOpen(true);
    },
  };
};

interface Props extends SlidingModalProps {
  emailAddress: string;
  onRegisterClick: () => void;
}

const ForgottenPasswordSlidingModal = (props: Props) => {
  const [loading, setLoading] = useState(false);
  const [emailAddress, setEmailAddress] = useState(props.emailAddress);
  const [errorMessage, setErrorMessage] = useState<ReactNode>(null);
  const [resetLinkSent, setResetLinkSent] = useState(false);
  const textBoxId = useId();
  useEffect(() => {
    if (props.isOpen) {
      setEmailAddress(props.emailAddress);
      setLoading(false);
      setErrorMessage('');
      setResetLinkSent(false);
    }
  }, [props.emailAddress, props.isOpen]);

  return (
    <SlidingModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      background="darkBlue"
      slideAmount={props.slideAmount}
    >
      <div
        className={clsx(
          'daymadeTypographySRegular',
          'withReducedSpacing',
          styles.container,
        )}
      >
        <h3 className={clsx('daymadeTypographyMBlack', styles.heading)}>
          Reset your password
        </h3>
        {resetLinkSent ? (
          <p>
            An email with a password reset link has been sent to{' '}
            <strong>{emailAddress.trim()}</strong>. The link will expire in{' '}
            {PASSWORD_RESET_VALIDITY_HOURS}h.
          </p>
        ) : (
          <div className={styles.formContainer}>
            <p>Enter your email to receive a password reset link</p>
            {errorMessage && (
              <ErrorMessage background="onDark">
                <span className={styles.errorIntro}>Whoops...</span>{' '}
                {errorMessage}
              </ErrorMessage>
            )}
            <div>
              <label className="daymadeTypographySMedium" htmlFor={textBoxId}>
                Email address
              </label>
              <input
                id={textBoxId}
                className="dm2026textBox dm2026textBoxOnDark"
                type="email"
                placeholder="name@example.com"
                autoComplete="email"
                autoCorrect="off"
                value={emailAddress}
                onChange={(e) => {
                  setEmailAddress(e.currentTarget.value);
                  setErrorMessage('');
                }}
              />
              <div className={styles.sideBySide}>
                <span>Don't have an account?</span>
                <Button
                  buttonSize="Small"
                  buttonStyle="TertiaryGrey2"
                  onClick={() => {
                    props.onClose();
                    props.onRegisterClick();
                  }}
                >
                  Register
                </Button>
              </div>
            </div>

            <Button
              buttonStyle="Primary"
              buttonSize="Small"
              fullWidth
              loading={loading}
              disabled={emailAddress.length === 0}
              onClick={async () => {
                if (resetLinkSent) {
                  props.onClose();
                  return;
                }

                if (!validEmail(emailAddress.trim())) {
                  setErrorMessage('Please provide a valid email address.');
                  return;
                }

                setLoading(true);
                setErrorMessage('');
                const response = await tryApi(
                  authApiClient.public.requestPasswordReset({
                    body: { emailAddress: emailAddress.trim() },
                  }),
                );
                setLoading(false);
                if (response.status === HttpStatusCode.OK) {
                  setResetLinkSent(true);
                } else if (response.status === HttpStatusCode.BAD_REQUEST) {
                  setErrorMessage(
                    passwordResetFriendlyErrorMessages[response.body.reason] ||
                      passwordResetErrorMessageFallback,
                  );
                } else if (response.status === 'error') {
                  // If we get an error that we don't know how to handle, it might be fatal. The sliding modal only has a single button, so lets close the modal to prevent the user getting stuck
                  props.onClose();

                  throw new Error(
                    'ForgottenPasswordSlidingModal received error when requesting a password reset',
                    { cause: response.error },
                  );
                }
              }}
            >
              Request Email Link
            </Button>
          </div>
        )}
      </div>
    </SlidingModal>
  );
};

export default ForgottenPasswordSlidingModal;
